import styled from '@emotion/styled';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import Slider from 'react-slick';
import cx from '@utils/cx.util';
import ProductCard from '../product-card';
import { useMediaQuery } from 'react-responsive';

const SLIDER_BREAKPOINT = {
  lg: 1280,
  md: 1024,
  sm: 640,
  xs: 475,
};
const ProductFeedSlider = ({ items, productCardProps }) => {
  const sliderRef = useRef<Slider>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderViews = {
    lg: useMediaQuery({ maxWidth: SLIDER_BREAKPOINT.lg, minWidth: SLIDER_BREAKPOINT.md }),
    md: useMediaQuery({ maxWidth: SLIDER_BREAKPOINT.md, minWidth: SLIDER_BREAKPOINT.sm }),
    sm: useMediaQuery({ maxWidth: SLIDER_BREAKPOINT.sm, minWidth: SLIDER_BREAKPOINT.xs }),
    xs: useMediaQuery({ maxWidth: SLIDER_BREAKPOINT.xs }),
  };

  const itemsCount = useMemo(() => items.length, [items]);

  const shouldSlide = itemsCount > 4;

  const visibleDots = useMemo(() => {
    console.log({ sliderViews });

    if (sliderViews.lg) return itemsCount - 2;
    else if (sliderViews.md) return itemsCount - 1;
    else if (sliderViews.sm || sliderViews.xs) return itemsCount;
    else return itemsCount - 3;
  }, [sliderViews, itemsCount]);

  return (
    <StyledSliderContainer className='tw-mx-auto tw-my-6 tw-max-w-[1440px]'>
      {!shouldSlide && (
        <div
          className={cx(`tw-hidden tw-gap-6`, {
            'sm:tw-flex': itemsCount < 3,
            'sm:tw-grid sm:tw-grid-cols-3': itemsCount === 3,
            'lg:tw-grid lg:tw-grid-cols-4': itemsCount === 4,
          })}>
          {items.map((item, i) => (
            <ProductCard
              key={item.title}
              item={item}
              displayPosition={i + 1}
              features={[]}
              activeColors={[]}
              activeColorGroups={[]}
              {...productCardProps}
            />
          ))}
        </div>
      )}

      <div className={cx({ 'sm:tw-hidden': itemsCount < 4, 'lg:tw-hidden': itemsCount === 4 })}>
        <Slider
          ref={sliderRef}
          slidesToShow={shouldSlide ? 4.25 : itemsCount}
          slidesToScroll={1}
          className='tw-mx-0'
          arrows={false}
          dots={false}
          beforeChange={(current, next) => setActiveSlide(Math.ceil(next))}
          infinite={false}
          swipe={shouldSlide}
          draggable={shouldSlide}
          variableWidth={false}
          initialSlide={0}
          responsive={[
            {
              breakpoint: SLIDER_BREAKPOINT.lg,
              settings: {
                slidesToShow: 3.25,
                draggable: true,
                swipe: true,
              },
            },
            {
              breakpoint: SLIDER_BREAKPOINT.md,
              settings: {
                slidesToShow: 2.25,
                draggable: true,
                swipe: true,
              },
            },
            {
              breakpoint: SLIDER_BREAKPOINT.sm,
              settings: {
                slidesToShow: 1.75,
                draggable: true,
                swipe: true,
              },
            },
            {
              breakpoint: SLIDER_BREAKPOINT.xs,
              settings: {
                slidesToShow: 1.25,
                draggable: true,
                swipe: true,
              },
            },
          ]}>
          {items.map((item, i) => (
            <div key={item.url + i} className='tw-px-2 tw-py-1'>
              <div className='tw-max-w-[400px]'>
                <InnerSliderProductCard
                  item={item}
                  displayPosition={i + 1}
                  features={[]}
                  activeColors={[]}
                  activeColorGroups={[]}
                  useVariantUrl={true}
                  className='tw-min-h-[470px]'
                  {...productCardProps}
                />
              </div>
            </div>
          ))}
        </Slider>
      </div>

      <div
        className={cx('tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-5', {
          'sm:tw-hidden': itemsCount < 4,
          'lg:tw-hidden': itemsCount === 4,
        })}>
        <HiArrowLeft
          size={18}
          className='tw-hidden tw-cursor-pointer lg:tw-block'
          onClick={() => sliderRef.current.slickGoTo(activeSlide === 0 ? visibleDots - 1 : activeSlide - 1)}
        />

        <div className='tw-flex tw-items-center tw-justify-center tw-gap-2'>
          {visibleDots >= 0 &&
            new Array(visibleDots).fill(0).map((_, idx) => (
              <div
                key={idx}
                className={classNames(
                  'tw-h-[8px] tw-w-[8px] tw-cursor-pointer tw-rounded-full tw-border tw-border-black tw-transition tw-duration-300',
                  {
                    'tw-bg-transparent hover:tw-bg-black/30': activeSlide !== idx,
                    'tw-bg-black': activeSlide === idx,
                  }
                )}
                onClick={() => sliderRef.current.slickGoTo(idx)}></div>
            ))}
        </div>

        <HiArrowRight
          size={18}
          className='tw-hidden tw-cursor-pointer lg:tw-block'
          onClick={() => sliderRef.current.slickGoTo(activeSlide === visibleDots - 1 ? 0 : activeSlide + 1)}
        />
      </div>
    </StyledSliderContainer>
  );
};

export default ProductFeedSlider;

const StyledSliderContainer = styled.div`
  .slick-dots {
    display: flex !important;
    width: 100%;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;

    li {
      margin: 0px !important;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 7px !important;
      height: 7px !important;

      button {
        width: 7px !important;
        height: 7px !important;
        &::before {
          width: 7px;
          height: 7px;
          background-color: var(--neutrals-light-grey);
        }
      }

      &.slick-active {
        button {
          height: 10px !important;
          width: 10px !important;

          &::before {
            height: 10px;
            width: 10px;
            background-color: var(--neutrals-graphite);
          }
        }
      }
    }
  }
`;

const InnerSliderProductCard = (props) => {
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    setRendered(true);
  }, []);

  if (!rendered) return null;

  return <ProductCard {...props} />;
};
